import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { clientApi, AskQuestionResp, JobStatus, MeResp, QuestionSetStatus, RefineResp } from '@/api/client'
import { qk } from '@/api/queryKeys'
import { orgScopedKey } from '@/auth/session'
import QueueConfirmationCard from './ask/QueueConfirmationCard'
import ScenarioEditor from './ask/ScenarioEditor'
import {
  AskMode,
  ASK_QUEUE_STORAGE_BASE_KEY,
  elapsed,
  etaLabel,
  inferSourceType,
  nextQueueId,
  nextSourceId,
  Phase,
  QueueItem,
  queueItemFromJob,
  sourceContextForItem,
  SourceRow,
  stageLabel,
} from './ask/askTypes'

interface AskInputProps {
  watchlistId?: string | null
  onItemCreated?: (itemId: string) => void
}

/* ---------------------------------------------------------------------------
 * Component
 * -------------------------------------------------------------------------*/

export default function AskInput({ watchlistId, onItemCreated }: AskInputProps) {
  const [question, setQuestion] = useState('')
  const [mode, setMode] = useState<AskMode>('single')
  const [sources, setSources] = useState<SourceRow[]>([])
  const [bulkInput, setBulkInput] = useState('')
  const [showBulk, setShowBulk] = useState(false)

  // Pull server-authored limits from /v1/me so the slider max / source
  // cap track CLIENT_ASK_SET_* env changes without redeploying the UI.
  // Defaults mirror the current envs as a fallback while /me is in flight.
  const meQuery = useQuery<MeResp>({
    queryKey: qk.me(),
    queryFn: () => clientApi.me(),
    staleTime: 60_000,
  })
  const serverLimits = meQuery.data?.ask_limits
  const MAX_QUESTIONS_LIMIT = Math.max(1, serverLimits?.set_max_questions ?? 12)
  const MAX_SOURCES_LIMIT = Math.max(1, serverLimits?.set_max_sources ?? 8)
  const [maxQuestions, setMaxQuestions] = useState<number>(6)
  // Re-clamp if the server cap comes back smaller than the current picker.
  useEffect(() => {
    setMaxQuestions((v) => Math.min(Math.max(1, v), MAX_QUESTIONS_LIMIT))
  }, [MAX_QUESTIONS_LIMIT])

  const [scenarioLabel, setScenarioLabel] = useState('')
  // Each entry is one user-authored assumption; empty array = no
  // scenario applied. Structured rows (instead of a newline-split
  // textarea) let us label, save, and preview them individually.
  const [assumptionsList, setAssumptionsList] = useState<string[]>([])
  // When the draft was Loaded from or Saved to a server-side scenario,
  // remember its id so the ask payload can bind this question to that
  // first-class scenario (avoids creating duplicates server-side).
  const [scenarioId, setScenarioId] = useState<string | null>(null)

  // Active single-question flow
  const [phase, setPhase] = useState<Phase>('idle')
  const [analysis, setAnalysis] = useState<AskQuestionResp | null>(null)
  const [refinement, setRefinement] = useState<RefineResp | null>(null)
  const [setStatus, setSetStatus] = useState<QuestionSetStatus | null>(null)
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [startMs, setStartMs] = useState(0)
  const [tick, setTick] = useState(0)

  // Multi-question queue
  const [queue, setQueue] = useState<QueueItem[]>([])

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Per-queue-item poll intervals, so we can clear them on unmount / cancel.
  const itemPollsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map())
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const clearItemPoll = (queueId: string) => {
    const iv = itemPollsRef.current.get(queueId)
    if (iv) {
      clearInterval(iv)
      itemPollsRef.current.delete(queueId)
    }
  }

  const buildScenario = () => {
    const assumptions = assumptionsList
      .map((x) => x.trim())
      .filter(Boolean)
      .slice(0, 20)
      .map((text, i) => ({ id: `a${i + 1}`, text }))
    if (assumptions.length === 0) return undefined
    const label = scenarioLabel.trim()
    return {
      assumption_mode: 'hard' as const,
      assumptions,
      ...(label ? { label } : {}),
      // Passing scenario_id lets the server bind this question to the
      // existing first-class scenario instead of resolving-or-creating
      // from (label, assumption) again. The backend treats the inline
      // payload as the source of truth when this is absent.
      ...(scenarioId ? { scenario_id: scenarioId } : {}),
    }
  }

  const recordUsageEvent = (action: string, meta_json?: Record<string, any>) => {
    void clientApi.recordUsageEvent({
      action,
      target_type: watchlistId ? 'watchlist' : 'ask_input',
      target_id: watchlistId || undefined,
      meta_json,
    }).catch(() => undefined)
  }

  /** Convert the UI source list to the API payload shape. */
  const buildSourcePayload = () => {
    return sources
      .map((s, idx) => {
        const value = String(s.value || '').trim()
        const label = String(s.label || '').trim() || `Source ${idx + 1}`
        return value ? { type: s.type, value, label } : null
      })
      .filter((s): s is { type: 'url' | 'text' | 'note'; value: string; label: string } => !!s)
  }

  const addSourceRow = (partial?: Partial<SourceRow>) => {
    setSources((prev) => [
      ...prev,
      {
        id: nextSourceId(),
        type: partial?.type || 'url',
        value: partial?.value || '',
        label: partial?.label || '',
      },
    ])
  }

  const updateSourceRow = (id: string, patch: Partial<SourceRow>) => {
    setSources((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)))
  }

  const removeSourceRow = (id: string) => {
    setSources((prev) => prev.filter((s) => s.id !== id))
  }

  /** Import multi-line bulk paste as N new structured source rows. */
  const importBulk = () => {
    const lines = bulkInput.split('\n').map((x) => x.trim()).filter(Boolean)
    if (lines.length === 0) return
    const rows: SourceRow[] = lines.map((value, idx) => ({
      id: nextSourceId(),
      type: inferSourceType(value),
      value,
      label: `Source ${sources.length + idx + 1}`,
    }))
    setSources((prev) => [...prev, ...rows])
    setBulkInput('')
    setShowBulk(false)
  }

  const activeScenario = buildScenario()

  // Cleanup
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (tickRef.current) clearInterval(tickRef.current)
      for (const iv of itemPollsRef.current.values()) clearInterval(iv)
      itemPollsRef.current.clear()
    }
  }, [])

  // Restore queue from localStorage and merge recent server jobs.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(orgScopedKey(ASK_QUEUE_STORAGE_BASE_KEY))
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) {
          const restored = parsed.filter((x) => x && typeof x === 'object') as QueueItem[]
          if (restored.length > 0) setQueue(restored)
        }
      }
    } catch { /* ignore */ }

    ;(async () => {
      try {
        // `ask_only=true` matches what Topbar + MyQuestions use, so all three
        // views agree on the same set of jobs. The single-question queue
        // only cares about `kind=forecast`, so filter those client-side to
        // avoid leaking forecast_source_set jobs into the single-question UI.
        const resp = await clientApi.jobs(undefined, 25, true)
        const serverItems = (resp.jobs || [])
          .filter((j) => String(j.kind || '') === 'forecast')
          .map(queueItemFromJob)
          .filter(Boolean) as QueueItem[]
        if (serverItems.length === 0) return
        setQueue((prev) => {
          const byJobId = new Map<string, QueueItem>()
          for (const item of prev) {
            if (item.jobId) byJobId.set(item.jobId, item)
          }
          const merged = [...prev]
          for (const srv of serverItems) {
            if (srv.jobId && byJobId.has(srv.jobId)) {
              const idx = merged.findIndex((x) => x.jobId === srv.jobId)
              if (idx >= 0) merged[idx] = { ...merged[idx], ...srv }
            } else {
              merged.push(srv)
            }
          }
          return merged.sort((a, b) => (b.startMs || 0) - (a.startMs || 0)).slice(0, 25)
        })
      } catch { /* ignore */ }
    })()
  }, [])

  // Persist queue locally (org-scoped).
  useEffect(() => {
    try {
      localStorage.setItem(orgScopedKey(ASK_QUEUE_STORAGE_BASE_KEY), JSON.stringify(queue.slice(0, 30)))
    } catch { /* ignore */ }
  }, [queue])

  // Stable signature of the active-job set, so this effect only re-binds when
  // the set of in-flight jobIds changes (not every time the queue mutates).
  const activeJobIdsKey = useMemo(
    () => queue
      .filter((q) => q.jobId && (q.status === 'forecasting' || q.status === 'queued'))
      .map((q) => q.jobId as string)
      .sort()
      .join('|'),
    [queue],
  )

  // Reconnect polling for in-flight forecast jobs after reload/navigation.
  useEffect(() => {
    if (!activeJobIdsKey) return
    const activeIds = activeJobIdsKey.split('|').filter(Boolean)
    let cancelled = false
    const poll = async () => {
      for (const jobId of activeIds) {
        try {
          const st = await clientApi.jobStatus(jobId)
          if (cancelled) return
          setQueue((prev) => prev.map((q) => {
            if (q.jobId !== jobId) return q
            if (st.status === 'succeeded') {
              return { ...q, status: 'done', prob: st.output_json?.probability ?? q.prob, itemId: st.item_id || q.itemId }
            }
            if (st.status === 'failed') {
              return { ...q, status: 'failed', error: st.error || q.error }
            }
            return { ...q, status: 'forecasting' }
          }))
        } catch { /* ignore */ }
      }
    }
    const iv = setInterval(poll, 5000)
    void poll()
    return () => { cancelled = true; clearInterval(iv) }
  }, [activeJobIdsKey])

  // Elapsed timer
  useEffect(() => {
    if (['refining', 'compiling', 'forecasting'].includes(phase)) {
      tickRef.current = setInterval(() => setTick((t) => t + 1), 1000)
    } else {
      if (tickRef.current) clearInterval(tickRef.current)
      tickRef.current = null
    }
    return () => { if (tickRef.current) clearInterval(tickRef.current); tickRef.current = null }
  }, [phase])

  /* --- Queue processor (parallel) ---------------------------------------
   *
   * Previously a single `queueProcessingRef` lock meant one ambiguous item
   * at the front of the queue would block everything behind it. We now
   * process up to MAX_PARALLEL_ITEMS queued items concurrently. Items
   * that come back from /refine with `needs_confirmation=true` move to
   * the `confirming` status and release their slot, so later queued items
   * can still proceed.
   *
   * `processingItemsRef` tracks which queue-item ids are in-flight *right
   * now* (between setQueue('queued'→'refining'...) and terminal state);
   * this dedupes the effect, which fires on every setQueue call.
   */
  const MAX_PARALLEL_ITEMS = 2
  const processingItemsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const active = processingItemsRef.current.size
      + queue.filter((q) => q.status === 'refining' || q.status === 'forecasting').length
    const budget = Math.max(0, MAX_PARALLEL_ITEMS - active)
    if (budget <= 0) return

    const nextBatch = queue.filter((q) => q.status === 'queued').slice(0, budget)
    if (nextBatch.length === 0) return

    for (const next of nextBatch) {
      if (processingItemsRef.current.has(next.id)) continue
      processingItemsRef.current.add(next.id)
      void processQueueItem(next.id, next.question)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue])

  /** Core per-item pipeline: refine → (auto)ask → poll until terminal. */
  async function processQueueItem(qid: string, questionText: string) {
    const updateItem = (patch: Partial<QueueItem>) =>
      setQueue((prev) => prev.map((q) => (q.id === qid ? { ...q, ...patch } : q)))

    try {
      updateItem({ status: 'refining', startMs: Date.now() })

      const refined = await clientApi.refineQuestion(questionText, activeScenario)
      updateItem({ status: 'confirming', refinement: refined })

      if (refined.needs_confirmation) {
        // Release the slot so later queued items aren't blocked waiting
        // for the human to pick options in this card.
        processingItemsRef.current.delete(qid)
        return
      }

      updateItem({ status: 'forecasting' })
      const resp = await clientApi.askQuestion(
        refined.analysis.normalized_question,
        watchlistId || undefined,
        activeScenario,
      )
      updateItem({ jobId: resp.job_id, itemId: resp.item_id })
      if (onItemCreated) onItemCreated(resp.item_id)
      queryClient.invalidateQueries({ queryKey: qk.jobs.all() })

      clearItemPoll(qid)
      const iv = setInterval(async () => {
        try {
          const st = await clientApi.jobStatus(resp.job_id)
          if (st.status === 'succeeded') {
            clearItemPoll(qid)
            processingItemsRef.current.delete(qid)
            updateItem({ status: 'done', prob: st.output_json?.probability ?? undefined })
          } else if (st.status === 'failed') {
            clearItemPoll(qid)
            processingItemsRef.current.delete(qid)
            updateItem({ status: 'failed', error: st.error || 'Failed' })
          }
        } catch { /* keep polling */ }
      }, 4000)
      itemPollsRef.current.set(qid, iv)
    } catch (ex: any) {
      processingItemsRef.current.delete(qid)
      updateItem({ status: 'failed', error: ex?.message || 'Failed' })
    }
  }

  /* --- Confirm a queued item ---
   * User hit "Confirm & forecast" on the confirmation card. We claim the
   * slot, re-refine (to apply user edits through the same pipeline) and
   * kick off the ask. If refinement still comes back ambiguous we drop
   * back to `confirming` so the user can iterate.
   */
  const confirmQueueItem = async (queueId: string, confirmedQuestion: string) => {
    processingItemsRef.current.add(queueId)
    const updateItem = (patch: Partial<QueueItem>) =>
      setQueue((prev) => prev.map((q) => (q.id === queueId ? { ...q, ...patch } : q)))

    updateItem({ status: 'refining', question: confirmedQuestion })

    try {
      const refined = await clientApi.refineQuestion(confirmedQuestion, activeScenario)
      if (refined.needs_confirmation) {
        updateItem({ status: 'confirming', refinement: refined, question: confirmedQuestion })
        processingItemsRef.current.delete(queueId)
        return
      }

      updateItem({ status: 'forecasting', question: refined.analysis.normalized_question, refinement: refined })
      const resp = await clientApi.askQuestion(refined.analysis.normalized_question, watchlistId || undefined, activeScenario)
      updateItem({ jobId: resp.job_id, itemId: resp.item_id })
      if (onItemCreated) onItemCreated(resp.item_id)
      queryClient.invalidateQueries({ queryKey: qk.jobs.all() })

      clearItemPoll(queueId)
      const iv = setInterval(async () => {
        try {
          const st = await clientApi.jobStatus(resp.job_id)
          if (st.status === 'succeeded') {
            clearItemPoll(queueId)
            processingItemsRef.current.delete(queueId)
            updateItem({ status: 'done', prob: st.output_json?.probability ?? undefined })
          } else if (st.status === 'failed') {
            clearItemPoll(queueId)
            processingItemsRef.current.delete(queueId)
            updateItem({ status: 'failed', error: st.error || 'Failed' })
          }
        } catch { /* keep polling */ }
      }, 4000)
      itemPollsRef.current.set(queueId, iv)
    } catch (ex: any) {
      processingItemsRef.current.delete(queueId)
      updateItem({ status: 'failed', error: ex?.message || 'Failed' })
    }
  }

  const clarifyQueueItem = async (queueId: string, clarifiedQuestion: string) => {
    processingItemsRef.current.add(queueId)
    const updateItem = (patch: Partial<QueueItem>) =>
      setQueue((prev) => prev.map((q) => (q.id === queueId ? { ...q, ...patch } : q)))

    updateItem({ status: 'refining', question: clarifiedQuestion })

    try {
      const refined = await clientApi.refineQuestion(clarifiedQuestion, activeScenario)
      updateItem({ status: 'confirming', refinement: refined, question: clarifiedQuestion })
    } catch (ex: any) {
      updateItem({ status: 'failed', error: ex?.message || 'Failed' })
    } finally {
      processingItemsRef.current.delete(queueId)
    }
  }

  /* --- Decompose categorical into multiple binary questions --- */
  const decomposeQueueItem = (queueId: string, binaryQuestions: string[]) => {
    setQueue((prev) => {
      const idx = prev.findIndex((q) => q.id === queueId)
      if (idx < 0) return prev
      const replacement: QueueItem[] = binaryQuestions.map((q) => ({
        id: nextQueueId(),
        question: q,
        status: 'queued',
      }))
      return [...prev.slice(0, idx), ...replacement, ...prev.slice(idx + 1)]
    })
    processingItemsRef.current.delete(queueId)
  }

  const cancelQueueItem = (queueId: string) => {
    clearItemPoll(queueId)
    processingItemsRef.current.delete(queueId)
    setQueue((prev) => prev.filter((q) => q.id !== queueId))
  }

  // Track fine-grained submit errors that don't fit in the shared
  // `error` string used by the source-set flow, so the single-question
  // input can show a duplicate-question warning without tripping the
  // whole source-set phase machine.
  const [singleSubmitError, setSingleSubmitError] = useState<string | null>(null)

  /** Normalise a question for duplicate detection. Whitespace-collapsed,
   *  case-folded, trailing punctuation trimmed — matches what the user
   *  would consider "the same question" even if they typed a trailing
   *  space or different casing. */
  function normalizeQuestion(q: string): string {
    return q.replace(/\s+/g, ' ').trim().replace(/[.?!]+$/, '').toLowerCase()
  }

  /* --- Submit handler --- */
  const handleSubmit = async () => {
    const q = question.trim()

    if (mode === 'single') {
      if (q.length < 3) return
      // Duplicate-question guard: only block if the same question is
      // currently *in flight* (queued / refining / confirming / forecasting).
      // A previously-completed `done` item is NOT a dup — the user may be
      // re-asking with a different scenario attached, which is exactly what
      // "Ask again" is for. Blocking `done` here created an infinite loop
      // where "Ask again" clones the text, submit is blocked with
      // "use Ask again", and the user can never actually re-run.
      const qKey = normalizeQuestion(q)
      const existing = queue.find(
        (qi) =>
          normalizeQuestion(qi.question) === qKey &&
          qi.status !== 'failed' &&
          qi.status !== 'done',
      )
      if (existing) {
        setSingleSubmitError('That question is already in the queue — check the list below.')
        return
      }
      setSingleSubmitError(null)
      recordUsageEvent('ask_started', {
        mode: 'single',
        has_scenario: Boolean(activeScenario),
        question_length: q.length,
      })
      setQueue((prev) => [...prev, { id: nextQueueId(), question: q, status: 'queued' }])
      setQuestion('')
      return
    }

    // Source-set mode
    const payloadSources = buildSourcePayload()
    if (payloadSources.length === 0) {
      setError('Add at least one source (URL or text)')
      setPhase('error')
      return
    }

    setPhase('compiling')
    setError(null)
    setAnalysis(null)
    setSetStatus(null)
    setJobStatus(null)
    setStartMs(Date.now())
    setTick(0)

    try {
      const scenario = buildScenario()
      recordUsageEvent('source_set_started', {
        mode: 'source_set',
        source_count: payloadSources.length,
        max_questions: Math.max(1, Math.min(MAX_QUESTIONS_LIMIT, maxQuestions)),
        has_scenario: Boolean(scenario),
      })
      const resp = await clientApi.askQuestionSet({
        watchlist_id: watchlistId || undefined,
        prompt: q || undefined,
        max_questions: Math.max(1, Math.min(MAX_QUESTIONS_LIMIT, maxQuestions)),
        scenario,
        sources: payloadSources,
      })
      setPhase('forecasting')
      queryClient.invalidateQueries({ queryKey: qk.jobs.all() })

      pollRef.current = setInterval(async () => {
        try {
          const [job, qs] = await Promise.all([
            clientApi.jobStatus(resp.job_id),
            clientApi.questionSetStatus(resp.set_id),
          ])
          setJobStatus(job)
          setSetStatus(qs)
          if (job.status === 'succeeded' || job.status === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current)
            pollRef.current = null
            queryClient.invalidateQueries({ queryKey: qk.jobs.all() })
            queryClient.invalidateQueries({ queryKey: qk.watchlists.all() })
            if (job.status === 'succeeded') {
              setPhase('done')
              // No auto-redirect: the user may want to stay on the
              // dashboard to kick off another set, or read the results
              // inline. The "done" card renders explicit "View set" and
              // "Ask another" CTAs further down.
            } else {
              setError(job.error || qs.error || 'Ask-set job failed')
              setPhase('error')
            }
          }
        } catch { /* keep polling */ }
      }, 4000)
    } catch (ex: any) {
      setError(ex?.message || 'Request failed')
      setPhase('error')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const resetSourceSet = () => {
    if (pollRef.current) clearInterval(pollRef.current)
    if (tickRef.current) clearInterval(tickRef.current)
    pollRef.current = null
    tickRef.current = null
    setPhase('idle')
    setSources([])
    setBulkInput('')
    setShowBulk(false)
    setAnalysis(null)
    setSetStatus(null)
    setJobStatus(null)
    setError(null)
    setStartMs(0)
    setTick(0)
  }

  const progressPct = (() => {
    if (mode === 'single') return undefined
    if (!setStatus) return undefined
    const gen = setStatus.generated_count || 0
    if (!gen) return undefined
    const done = (setStatus.forecasted_count || 0) + (setStatus.failed_count || 0)
    return Math.min(100, Math.round((done / gen) * 100))
  })()

  const activeQueue = queue.filter((q) => q.status !== 'done' || Date.now() - (q.startMs || 0) < 60_000)
  const hasActiveWork = activeQueue.length > 0
  // Confirmation cards live in their own "Needs clarification" section
  // directly under the scenario editor so users don't have to scroll
  // past the Tracked Questions list to reach them.
  const confirmingItems = queue.filter((q) => q.status === 'confirming' && q.refinement)

  return (
    <div className="ask-container">
      <div className="ask-mode-row">
        <button
          className={`btn ${mode === 'single' ? 'primary' : ''}`}
          onClick={() => setMode('single')}
          disabled={phase !== 'idle' && mode !== 'single'}
          type="button"
          title="Ask one forecasting question"
        >
          Single Question
        </button>
        <button
          className={`btn ${mode === 'set' ? 'primary' : ''}`}
          onClick={() => setMode('set')}
          disabled={
            (phase !== 'idle' && mode !== 'set')
            || (!!serverLimits && !serverLimits.ask_set_enabled)
          }
          type="button"
          title={
            serverLimits && !serverLimits.ask_set_enabled
              ? 'Source-set asking is disabled for your org'
              : 'Paste sources and generate multiple forecast questions from them'
          }
        >
          From Sources
        </button>
        <span className="ask-mode-hint">
          {mode === 'single'
            ? 'Ask a yes/no question with a deadline. The system will research and forecast it.'
            : 'Paste URLs or text. The system will generate relevant forecast questions and answer them.'}
        </span>
      </div>

      {/* Input row — always visible so user can keep adding questions */}
      <div className="ask-input-row">
        <input
          className="input ask-input"
          type="text"
          placeholder={mode === 'single' ? 'e.g. Will Apple announce a new AI chip by December 2026?' : 'e.g. Focus on supply chain risks and pricing impact'}
          value={question}
          onChange={(e) => { setQuestion(e.target.value); if (singleSubmitError) setSingleSubmitError(null) }}
          onKeyDown={handleKeyDown}
          aria-invalid={!!singleSubmitError}
          aria-describedby={singleSubmitError ? 'ask-single-error' : undefined}
        />
        <button
          className="btn primary ask-btn"
          onClick={handleSubmit}
          disabled={mode === 'single' ? question.trim().length < 3 : (phase !== 'idle')}
        >
          {mode === 'single' ? (hasActiveWork ? 'Add' : 'Ask') : (phase === 'compiling' ? <span className="ask-spinner" /> : 'Ask')}
        </button>
      </div>

      {singleSubmitError && mode === 'single' && (
        <div
          id="ask-single-error"
          className="muted"
          role="alert"
          style={{ fontSize: 12, marginTop: 4, color: 'var(--warn)' }}
        >
          {singleSubmitError}
        </div>
      )}

      {mode === 'set' && (
        <div className="ask-extra-card">
          <div className="ask-extra-label">
            Sources
            <span className="ask-extra-hint"> — pick a type per source and (optionally) label it</span>
          </div>

          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {sources.map((s, idx) => (
              <div
                key={s.id}
                className="row"
                style={{
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '8px',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <select
                  className="input"
                  style={{ flex: '0 0 90px', fontSize: 12 }}
                  value={s.type}
                  onChange={(e) => updateSourceRow(s.id, { type: e.target.value as SourceRow['type'] })}
                  disabled={phase !== 'idle'}
                  aria-label={`Source ${idx + 1} type`}
                >
                  <option value="url">URL</option>
                  <option value="text">Text</option>
                  <option value="note">Note</option>
                </select>

                <div style={{ flex: 1, display: 'grid', gap: 6 }}>
                  <input
                    className="input"
                    type="text"
                    placeholder={s.type === 'url' ? 'https://example.com/report' : 'Paste text, transcript, or note…'}
                    value={s.value}
                    onChange={(e) => updateSourceRow(s.id, { value: e.target.value })}
                    disabled={phase !== 'idle'}
                    aria-label={`Source ${idx + 1} value`}
                  />
                  <input
                    className="input"
                    type="text"
                    placeholder={`Label (optional — defaults to "Source ${idx + 1}")`}
                    value={s.label}
                    onChange={(e) => updateSourceRow(s.id, { label: e.target.value })}
                    disabled={phase !== 'idle'}
                    style={{ fontSize: 12 }}
                    aria-label={`Source ${idx + 1} label`}
                  />
                </div>

                <button
                  className="btn small"
                  type="button"
                  onClick={() => removeSourceRow(s.id)}
                  disabled={phase !== 'idle'}
                  aria-label={`Remove source ${idx + 1}`}
                  title="Remove this source"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button
              className="btn small"
              type="button"
              onClick={() => addSourceRow()}
              disabled={phase !== 'idle' || sources.length >= MAX_SOURCES_LIMIT}
              title={sources.length >= MAX_SOURCES_LIMIT ? `Maximum ${MAX_SOURCES_LIMIT} sources per set` : 'Add a new source'}
            >
              + Add source
            </button>
            <button
              className="btn small"
              type="button"
              onClick={() => setShowBulk((s) => !s)}
              disabled={phase !== 'idle'}
            >
              {showBulk ? 'Hide bulk paste' : 'Paste multiple at once'}
            </button>
            <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
              {sources.length}/{MAX_SOURCES_LIMIT} sources
            </span>
          </div>

          {showBulk && (
            <div style={{ marginTop: 8 }}>
              <textarea
                className="input ask-textarea"
                placeholder={'https://example.com/report\nManagement said demand improved in Q1…'}
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                disabled={phase !== 'idle'}
                rows={4}
                aria-label="Bulk paste sources"
              />
              <div className="row" style={{ gap: 8, marginTop: 6 }}>
                <button
                  className="btn small primary"
                  type="button"
                  onClick={importBulk}
                  disabled={phase !== 'idle' || bulkInput.trim().length === 0}
                >
                  Import lines as sources
                </button>
              </div>
            </div>
          )}

          {/* max_questions slider */}
          <div className="row" style={{ gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="max-questions-range" className="ask-extra-label" style={{ marginBottom: 0 }}>
              Generate up to
            </label>
            <input
              id="max-questions-range"
              type="range"
              min={1}
              max={MAX_QUESTIONS_LIMIT}
              step={1}
              value={maxQuestions}
              onChange={(e) => setMaxQuestions(Number(e.target.value))}
              disabled={phase !== 'idle'}
              style={{ flex: '1 1 180px', minWidth: 160 }}
              aria-valuemin={1}
              aria-valuemax={MAX_QUESTIONS_LIMIT}
              aria-valuenow={maxQuestions}
              aria-label="Maximum number of questions to generate"
            />
            <span className="mono" style={{ minWidth: 28, textAlign: 'right' }}>{maxQuestions}</span>
            <span className="muted" style={{ fontSize: 12 }}>questions per set</span>
          </div>
        </div>
      )}

      <ScenarioEditor
        label={scenarioLabel}
        onLabelChange={setScenarioLabel}
        assumptions={assumptionsList}
        onAssumptionsChange={setAssumptionsList}
        scenarioId={scenarioId}
        onScenarioIdChange={setScenarioId}
        targetQuestion={mode === 'single' ? question : null}
        disabled={phase !== 'idle'}
      />

      {/* Active scenario reminder — shown ONLY while an ask is in flight,
          so the user knows their conditioning is being applied. */}
      {activeScenario && (hasActiveWork || phase !== 'idle') && (
        <div className="ask-scenario-card" role="note">
          <div className="ask-scenario-header">
            <span className="pill">Conditional forecast active</span>
            {activeScenario.label && <span className="ask-scenario-label">{activeScenario.label}</span>}
          </div>
          <div className="ask-scenario-assumptions">
            <div className="ask-extra-label">Treated as true for this forecast:</div>
            <ul className="ask-scenario-list">
              {activeScenario.assumptions.map((a) => (
                <li key={a.id}>{a.text}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* === NEEDS CLARIFICATION ===
          Any queue items flagged `confirming` (the copilot wants user
          input before forecasting) are lifted out of the Tracked
          Questions list and rendered here instead — between the
          scenario card and the queue — so users aren't forced to
          scroll past a long queue to reach the review card. Each
          confirming item also still appears as a compact row down in
          Tracked Questions so the overall status remains visible. */}
      {mode === 'single' && confirmingItems.length > 0 && (
        <div
          className="ask-queue"
          role="region"
          aria-label="Questions that need clarification before forecasting"
          style={{ borderLeft: '3px solid var(--warn)' }}
        >
          <div className="ask-queue-header">
            <span className="ask-queue-title">
              Needs clarification{confirmingItems.length > 1 ? ` (${confirmingItems.length})` : ''}
            </span>
            <span className="ask-queue-count" style={{ color: 'var(--muted)' }}>
              Review before forecasting
            </span>
          </div>
          {confirmingItems.map((item) => (
            <div key={item.id} className="ask-queue-item">
              <div className="ask-queue-item-main">
                <span
                  className="pill"
                  style={{ fontSize: 10, flexShrink: 0 }}
                  aria-label={`Status: ${item.status}`}
                >
                  <span aria-hidden="true" style={{ marginRight: 4 }}>?</span>
                  needs review
                </span>
                <span className="ask-queue-item-q">{item.question}</span>
                <button
                  className="btn small"
                  type="button"
                  title="Remove this question from the queue"
                  aria-label={`Remove "${item.question.slice(0, 40)}"`}
                  onClick={() => cancelQueueItem(item.id)}
                >
                  Remove
                </button>
              </div>
              {item.refinement && (
                <QueueConfirmationCard
                  item={item}
                  onClarify={(q) => clarifyQueueItem(item.id, q)}
                  onConfirm={(q) => confirmQueueItem(item.id, q)}
                  onDecompose={(qs) => decomposeQueueItem(item.id, qs)}
                  onCancel={() => cancelQueueItem(item.id)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* === TRACKED QUESTIONS (single question mode) === */}
      {mode === 'single' && queue.length > 0 && (
        <div className="ask-queue">
          <div className="ask-queue-header">
            <span className="ask-queue-title">Tracked Questions</span>
            <span className="ask-queue-count">
              {queue.filter((q) => q.status === 'done').length}/{queue.length} complete
            </span>
          </div>
          {queue.map((item) => (
            <div key={item.id} className="ask-queue-item">
              <div className="ask-queue-item-main">
                <span
                  className={`pill ${item.status === 'done' ? 'ok' : item.status === 'failed' ? 'bad' : ''}`}
                  style={{ fontSize: 10, flexShrink: 0 }}
                  aria-label={`Status: ${item.status}`}
                >
                  {/* Leading glyph so status isn't communicated by color alone */}
                  <span aria-hidden="true" style={{ marginRight: 4 }}>
                    {item.status === 'done' ? '\u2713'
                      : item.status === 'failed' ? '!'
                      : item.status === 'confirming' ? '?'
                      : '\u2022'}
                  </span>
                  {item.status === 'queued' ? 'queued'
                    : item.status === 'refining' ? 'analyzing...'
                    : item.status === 'confirming' ? 'needs review'
                    : item.status === 'forecasting' ? 'forecasting...'
                    : item.status === 'done' ? 'done'
                    : 'failed'}
                </span>
                <span className="ask-queue-item-q">{item.question}</span>
                {typeof item.prob === 'number' && (
                  <span className="ask-queue-item-prob">{Math.round(item.prob * 100)}%</span>
                )}
                {item.status === 'forecasting' && item.startMs && (
                  <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{elapsed(item.startMs)}</span>
                )}
                {item.status === 'confirming' && (
                  <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    See &ldquo;Needs clarification&rdquo; above &uarr;
                  </span>
                )}
                {item.itemId && item.status === 'done' && (
                  <button className="btn small" onClick={() => navigate(`/items/${item.itemId}`)} type="button">
                    View
                  </button>
                )}
                {item.status === 'done' && (
                  <button
                    className="btn small"
                    type="button"
                    title="Re-run this question with the current scenario"
                    onClick={() => {
                      // "Ask again": clone the question into the input so
                      // the user can tweak it first rather than silently
                      // firing a second identical job. Avoids wasting LLM
                      // cycles when the user actually wanted to edit.
                      setQuestion(item.question)
                      setSingleSubmitError(null)
                    }}
                  >
                    Ask again
                  </button>
                )}
                {item.status === 'failed' && (
                  <button
                    className="btn small"
                    type="button"
                    title="Edit this question and re-run it"
                    onClick={() => {
                      setQuestion(item.question)
                      setSingleSubmitError(null)
                      cancelQueueItem(item.id)
                    }}
                  >
                    Edit &amp; retry
                  </button>
                )}
                {(item.status === 'queued' || item.status === 'failed') && (
                  <button
                    className="btn small"
                    type="button"
                    title="Remove this question from the queue"
                    aria-label={`Remove "${item.question.slice(0, 40)}"`}
                    onClick={() => cancelQueueItem(item.id)}
                  >
                    Remove
                  </button>
                )}
              </div>

              {item.status === 'failed' && item.error && (
                <div className="ask-queue-item-error" role="alert">{item.error}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* === SOURCE-SET progress (existing flow) === */}
      {mode === 'set' && (phase === 'compiling' || phase === 'forecasting' || phase === 'done') && (
        <div className="ask-progress-card">
          <div className="ask-progress-header">
            <span className="ask-progress-title">Building forecast set from sources</span>
            {setStatus?.set_id && (
              <button className="btn small" type="button" onClick={() => navigate(`/question-sets/${setStatus.set_id}`)}>
                View set
              </button>
            )}
          </div>

          {setStatus && (
            <div className="ask-set-counts">
              <span className="ask-set-count"><span className="ask-set-count-num">{setStatus.generated_count || 0}</span> generated</span>
              <span className="ask-set-count"><span className="ask-set-count-num">{setStatus.forecasted_count || 0}</span> forecasted</span>
              {(setStatus.failed_count || 0) > 0 && (
                <span className="ask-set-count bad"><span className="ask-set-count-num">{setStatus.failed_count}</span> failed</span>
              )}
            </div>
          )}

          {/* Per-source ingestion status — visible as soon as sources start
              ingesting, so the user gets feedback before any questions
              are generated. */}
          {setStatus && Array.isArray(setStatus.sources) && setStatus.sources.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
                Sources
              </div>
              <div className="stack" style={{ gap: 6 }}>
                {setStatus.sources.map((src: any, idx: number) => {
                  const ingestStatus = String(src?.ingest_status || 'pending').toLowerCase()
                  const isReady = ingestStatus === 'succeeded' || ingestStatus === 'ingested' || ingestStatus === 'ready'
                  const isFailed = ingestStatus === 'failed' || ingestStatus === 'error'
                  const pillCls = isReady ? 'pill ok' : isFailed ? 'pill bad' : 'pill'
                  const statusLabel = isReady ? 'ingested' : isFailed ? 'failed' : (ingestStatus || 'pending')
                  const label = String(src?.source_label || src?.input_value || `Source ${idx + 1}`)
                  const href = String(src?.source_type || '') === 'url' ? String(src?.input_value || '').trim() : ''
                  return (
                    <div
                      key={src?.source_asset_id || idx}
                      className="row"
                      style={{ gap: 8, alignItems: 'center', fontSize: 12, lineHeight: 1.45, flexWrap: 'wrap' }}
                    >
                      <span className={pillCls} style={{ fontSize: 10, flexShrink: 0 }}>{statusLabel}</span>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {href ? (
                          <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>
                            {label}
                          </a>
                        ) : (
                          label
                        )}
                      </span>
                      {src?.ingest_error && (
                        <span className="muted" style={{ fontSize: 11, color: 'var(--bad)' }} title={String(src.ingest_error)}>
                          {String(src.ingest_error).slice(0, 80)}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {phase === 'forecasting' && (
            <div
              className="ask-progress-bar-wrap"
              role="status"
              aria-live="polite"
              aria-atomic="false"
              aria-label="Source set forecast progress"
            >
              <div className="ask-stage-row">
                <span className="ask-stage-label">{stageLabel(mode, phase, jobStatus, setStatus)}</span>
                {startMs > 0 && <span className="ask-elapsed">{elapsed(startMs)}</span>}
              </div>
              <div
                className="ask-progress-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progressPct ?? undefined}
              >
                {progressPct !== undefined
                  ? <div className="ask-progress-bar-fill determinate" style={{ width: `${Math.max(4, progressPct)}%` }} />
                  : <div className="ask-progress-bar-fill" />}
              </div>
              {(() => { const eta = etaLabel(mode, setStatus, startMs); return eta ? <div className="ask-eta">{eta}</div> : null })()}
              <div className="ask-bg-note">You can leave this page. Results will continue in the background.</div>
            </div>
          )}

          {mode === 'set' && setStatus && Array.isArray(setStatus.items) && setStatus.items.length > 0 && (
            <div className="ask-set-items">
              {setStatus.items.map((item: any, idx: number) => {
                const sourceContext = sourceContextForItem(setStatus, item)
                return (
                  <div key={item.set_item_id || idx} className="ask-set-item">
                    <div className="ask-set-item-q">{item.question_spec_json?.question_text || item.question_text || `Question ${idx + 1}`}</div>
                    {sourceContext.length > 0 && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                        From:{' '}
                        {sourceContext.map((src: any, srcIdx: number) => {
                          const label = String(src?.source_label || src?.input_value || `Source ${srcIdx + 1}`)
                          const href = src?.source_type === 'url' ? String(src?.input_value || '').trim() : ''
                          return (
                            <React.Fragment key={src?.source_asset_id || srcIdx}>
                              {srcIdx > 0 ? ', ' : null}
                              {href ? (
                                <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{label}</a>
                              ) : (
                                <span>{label}</span>
                              )}
                            </React.Fragment>
                          )
                        })}
                      </div>
                    )}
                    <div className="ask-set-item-meta">
                      <span className={`pill ${item.status === 'succeeded' ? 'ok' : item.status === 'failed' ? 'bad' : ''}`}>{item.status || 'pending'}</span>
                      {item.forecast_output_json?.probability != null && (
                        <span className="ask-set-item-prob">
                          {typeof item.forecast_output_json.probability === 'number'
                            ? `${Math.round(item.forecast_output_json.probability * 100)}%`
                            : item.forecast_output_json.probability}
                        </span>
                      )}
                      {item.item_id && item.status === 'succeeded' && (
                        <button className="btn small" onClick={() => navigate(`/items/${item.item_id}`)} type="button">View</button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {phase === 'done' && (
            <div className="ask-done">
              <span className="pill ok">Forecast complete</span>
              {setStatus?.set_id && (
                <button
                  className="btn small primary"
                  type="button"
                  onClick={() => navigate(`/question-sets/${setStatus.set_id}`)}
                >
                  View set
                </button>
              )}
              <button className="btn small" onClick={(e) => { e.stopPropagation(); resetSourceSet() }} type="button">Ask another</button>
            </div>
          )}
        </div>
      )}

      {phase === 'error' && (
        <div className="ask-error-row">
          <span className="pill bad">{error || 'Something went wrong'}</span>
          <button className="btn" onClick={resetSourceSet}>Try again</button>
        </div>
      )}
    </div>
  )
}
